import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where, doc, setDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";
import { loadGradeSections, loadSectionAdvisers } from "../../services/sectionsService";
import { useLocation } from "react-router-dom";

function splitSections(obj) {
  if (Array.isArray(obj)) return obj.map(s => String(s).trim()).filter(Boolean);
  if (obj && Array.isArray(obj.sections)) return obj.sections.map(s => String(s).trim()).filter(Boolean);
  return String(obj || "").split(",").map(s => s.trim()).filter(Boolean);
}
function formatName(s) {
  const last = String(s.lastName || "").trim();
  const first = String(s.firstName || "").trim();
  const middle = String(s.middleName || s.middleInitial || "").trim();
  const core = `${last}${last && first ? ", " : ""}${first}`;
  return middle ? `${core} ${middle}` : core;
}
function today() { return new Date().toISOString().slice(0,10); }
function campusVariants(campus) {
  const c = String(campus || "").trim();
  const lc = c.toLowerCase();
  if (!lc) return [];
  if (lc === "gapan") return ["gapan", "Gapan", "gapan city", "Gapan City"];
  if (lc === "bulacan") return ["bulacan", "Bulacan"];
  return Array.from(new Set([lc, c]));
}

export default function Attendance() {
  const user = getCurrentUser();
  const params = new URLSearchParams(useLocation().search);
  const role = String(user?.role || "").toLowerCase();
  const [campus, setCampus] = useState(String(user?.campus || "gapan").toLowerCase());
  const isAdmin = role === "admin" || role === "superadmin";
  const [grade, setGrade] = useState(params.get("grade") || "");
  const [section, setSection] = useState(params.get("section") || "");
  const [date, setDate] = useState(today());
  const [students, setStudents] = useState([]);
  const [present, setPresent] = useState({});
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [sectionsMap, setSectionsMap] = useState({});
  const [teacherName, setTeacherName] = useState("");

  useEffect(() => {
    (async () => {
      // Try to get teacher display name from employees
      try {
        const es = await getDocs(collection(db, "employees"));
        const rows = es.docs.map(d => ({ id: d.id, ...d.data() }));
        const me = rows.find(e => String(e.email || "").toLowerCase() === String(user?.email || "").toLowerCase());
        if (me?.name) setTeacherName(me.name);
      } catch { /* ignore */ }
      const m = await loadGradeSections(campus);
      let map = m || {};
      if (!isAdmin) {
        // Filter by advisories
        try {
          const adv = await loadSectionAdvisers(campus);
          const out = {};
          const tName = (teacherName || user?.displayName || "").trim();
          if (tName && adv) {
            Object.keys(adv || {}).forEach(g => {
              const secObj = adv[g] || {};
              const mine = Object.keys(secObj).filter(s => String(secObj[s] || "").trim() === tName);
              if (mine.length) out[g] = mine;
            });
            if (Object.keys(out).length) map = out;
          }
        } catch { /* ignore */ }
      }
      setSectionsMap(map);
    })();
  }, [campus, isAdmin, user?.email, user?.displayName, teacherName]);

  const grades = useMemo(() => Object.keys(sectionsMap || {}), [sectionsMap]);
  const sections = useMemo(() => (sectionsMap?.[grade]) || [], [sectionsMap, grade]);

  useEffect(() => {
    (async () => {
      if (!campus || !grade) { setStudents([]); return; }
      const base = collection(db, "students");
      const all = new Map();
      for (const c of campusVariants(campus)) {
        let q1 = query(base, where("campus", "==", c), where("gradeLevel", "==", grade));
        const snap1 = await getDocs(q1);
        snap1.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
        if (section) {
          const q2 = query(base, where("campus", "==", c), where("gradeLevel", "==", grade), where("sections", "array_contains", section));
          const snap2 = await getDocs(q2);
          snap2.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
          const q3 = query(base, where("campus", "==", c), where("gradeLevel", "==", grade), where("section", "==", section));
          const snap3 = await getDocs(q3);
          snap3.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
          const q4 = query(base, where("campus", "==", c), where("gradeLevel", "==", grade), where("sections", "==", section));
          const snap4 = await getDocs(q4);
          snap4.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
        }
      }
      const rows = Array.from(all.values()).sort((a,b)=>formatName(a).localeCompare(formatName(b)));
      setStudents(rows);
      setPresent(Object.fromEntries(rows.map(r => [r.id, true])));
    })();
  }, [campus, grade, section, date]);

  function exportCSV() {
    const headers = ["ID","Full Name","Grade","Section","Date","Present"];
    const rows = students.map(s => [
      s.id, formatName(s), s.gradeLevel || "", splitSections(s.section).join(" / "), date, present[s.id] ? "Yes" : "No"
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `attendance_${campus}_${grade}_${section || "all"}_${date}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  async function save() {
    if (!campus || !grade) return;
    setSaving(true); setInfo("Saving attendance…");
    try {
      const id = `${campus}__${grade.replace(/\s+/g,"_")}__${(section||"").replace(/\s+/g,"_")}__${date}`;
      const ref = doc(db, "attendance", id);
      const payload = {
        campus, grade, section: section || "", date,
        recordedByUid: user?.uid || "",
        recordedByEmail: user?.email || "",
        recordedAt: new Date().toISOString(),
        students: students.map(s => ({ id: s.id, name: formatName(s), present: !!present[s.id] }))
      };
      await setDoc(ref, payload, { merge: true });
      setInfo("Attendance saved.");
    } catch (e) {
      setInfo("Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Attendance / Advisory List</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          {isAdmin && (
            <div className="col-md-2">
              <label className="form-label">Campus</label>
              <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
                <option value="gapan">Gapan</option>
                <option value="bulacan">Bulacan</option>
              </select>
            </div>
          )}
          <div className="col-md-2">
            <label className="form-label">Grade</label>
            <select className="form-select" value={grade} onChange={e => { setGrade(e.target.value); setSection(""); }}>
              <option value="">Select</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Section</label>
            <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
              <option value="">All</option>
              {sections.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Date</label>
            <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="col-md-4 d-flex gap-2">
            <button className="btn btn-primary" onClick={save} disabled={saving}>Save</button>
            <button className="btn btn-outline-secondary" onClick={exportCSV}>Export CSV</button>
            <button className="btn btn-outline-primary" onClick={() => window.print()}>Print</button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th style={{width:80}}>Present</th><th>Name</th><th>Grade</th><th>Section</th></tr></thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id}>
                  <td>
                    <input type="checkbox" className="form-check-input" checked={!!present[s.id]} onChange={e => setPresent(prev => ({ ...prev, [s.id]: e.target.checked }))} />
                  </td>
                  <td>{formatName(s)}</td>
                  <td>{s.gradeLevel || ""}</td>
                  <td>{splitSections(s.section).join(", ")}</td>
                </tr>
              ))}
              {students.length === 0 && <tr><td colSpan="4" className="text-center text-muted">No students found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`@media print { @page { size: letter portrait; margin: 12mm; } .btn, .form-select, input[type=date] { display: none !important; } }`}</style>
      {info && <div className="alert alert-info mt-3">{info}</div>}
    </div>
  );
}
